import {t, useLanguage, getLanguage} from './i18n';
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { firebaseAuth } from "./firebase";
import { isIosCapacitor } from "./utils/nativeHttp";
import { clearLoopAuthState, persistManualAuth } from "./utils/manualAuth";
import { getLoginRedirectTarget, schedulePostAuthRedirect } from "./auth/authNavigation";
import { FirebaseNative, isFirebaseNativeAvailable } from "./native/firebaseNative";
import { StatusBar } from "@capacitor/status-bar";
import { Eye, EyeOff } from "lucide-react";
import {
  signInWithAppleAcrossPlatforms,
  signInWithGoogleAcrossPlatforms,
  resolveCurrentAuthProfile,
  socialAuthErrorMessage,
} from "./auth/socialAuth";


function friendlyError(e) {
  const raw = (() => {
    try {
      return String(e?.code || e?.message || e || "");
    } catch (_) {
      return "";
    }
  })();

  const msg = raw.toLowerCase();

  if (
    msg.includes("auth/invalid-credential") ||
    msg.includes("auth/wrong-password") ||
    msg.includes("auth/user-not-found") ||
    msg.includes("invalid_login_credentials") ||
    msg.includes("password is invalid")
  ) {
    return "Usuario o contraseña incorrectos. Revisa tus datos e intenta nuevamente.";
  }

  if (msg.includes("auth/invalid-email")) {
    return "El usuario o correo no tiene un formato válido.";
  }

  if (msg.includes("auth/too-many-requests")) {
    return "Hiciste varios intentos seguidos. Espera unos minutos e intenta nuevamente.";
  }

  if (msg.includes("auth/network-request-failed") || msg.includes("network")) {
    return "No pudimos conectarnos. Revisa tu internet e intenta nuevamente.";
  }

  if (msg.includes("popup-closed") || msg.includes("cancelled") || msg.includes("canceled")) {
    return "Inicio de sesión cancelado.";
  }

  if (msg.includes("timeout")) {
    return "La conexión tardó demasiado. Intenta nuevamente.";
  }

  if (msg.includes("credential manager") || msg.includes("google login nativo") || msg.includes("sha-1") || msg.includes("sha-256")) {
    return "No pudimos iniciar sesión con Google en este dispositivo. Revisa la configuración de Google/Firebase e intenta nuevamente.";
  }

  if (msg.includes("firebasenative plugin")) {
    return "Esta versión de la app necesita una actualización para completar el inicio de sesión.";
  }

  return "No pudimos iniciar sesión. Intenta nuevamente.";
}

function withTimeout(promise, ms, label = "timeout") {
  let timer = null;
  const t = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(label)), ms);
  });

  return Promise.race([
    promise.finally(() => {
      try {
        if (timer) clearTimeout(timer);
      } catch (_) {}
    }),
    t,
  ]);
}

function decodeJwtPayload(token) {
  try {
    if (!token || typeof token !== "string") return null;
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64.padEnd(Math.ceil(b64.length / 4) * 4, "=");
    const json = atob(padded);
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
}

function extractIdentityFromIdToken(idToken) {
  const payload = decodeJwtPayload(idToken) || {};
  return {
    uid: payload.user_id || payload.uid || payload.sub || null,
    email: payload.email || payload.firebase?.identities?.email?.[0] || "",
    displayName: payload.name || payload.displayName || "",
    emailVerified:
      payload.email_verified === true ||
      payload.emailVerified === true ||
      payload.firebase?.sign_in_provider === "google.com" ||
      payload.firebase?.sign_in_provider === "apple.com",
  };
}

function broadcastManualAuthChange(type = "manual-auth-changed") {
  try {
    window.dispatchEvent(new Event(type));
  } catch (_) {}
}

function isPasswordProviderUser(user) {
  try {
    return Array.isArray(user?.providerData) && user.providerData.some((p) => p?.providerId === "password");
  } catch (_) {
    return false;
  }
}

async function redirectIfEmailNotVerified({ user, navigate, setError }) {
  try {
    if (!user || !user.email) return false;

    // Only block email/password accounts. Google/Apple are already verified by provider.
    if (!isPasswordProviderUser(user)) return false;

    try {
      await user.reload();
    } catch (_) {}

    if (user.emailVerified === true) return false;

    try {
      window.localStorage.setItem("pending_uid", user.uid);
      window.localStorage.setItem("pending_email", user.email);
    } catch (_) {}

    clearLoopAuthState({ keepPendingRegistration: true });

    setError?.("Debes verificar tu correo antes de ingresar.");

    navigate("/enter-code", {
      replace: true,
      state: {
        uid: user.uid,
        email: user.email,
      },
    });

    return true;
  } catch (_) {
    return false;
  }
}

function getCapacitorPlatform() {
  try {
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    if (!cap) return "web";
    if (typeof cap.getPlatform === "function") return cap.getPlatform();
    return cap.platform || "web";
  } catch (_) {
    return "web";
  }
}

function GoogleIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 19.5-8.9 19.5-20c0-1.2-.1-2.3-.3-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.3l-6.3-5.3C29.2 35 26.7 36 24 36c-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C9.6 39.5 16.3 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.1-4.1 5.4l6.3 5.3C37.1 39.1 44 34 44 24c0-1.2-.1-2.3-.4-3.5z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.7 13.1c0-2.5 2.1-3.7 2.2-3.8-1.2-1.7-3-1.9-3.6-2-1.5-.2-3 .9-3.8.9s-2-.9-3.3-.8c-1.7 0-3.2 1-4.1 2.5-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.2 2.5 1.3-.1 1.8-.8 3.3-.8s2 .8 3.3.8c1.4 0 2.3-1.2 3.1-2.5 1-1.4 1.4-2.8 1.4-2.9-.1-.1-2.9-1.2-3-4.1z" />
      <path d="M14.3 5.7c.7-.9 1.2-2.1 1.1-3.3-1.1 0-2.3.7-3.1 1.6-.7.8-1.3 2-1.1 3.2 1.2.1 2.4-.6 3.1-1.5z" />
    </svg>
  );
}

function readLoginHandoffWindow() {
  try {
    return Boolean(
      typeof window !== "undefined" &&
      window.__LOOP_LOGIN_HANDOFF__ &&
      window.__LOOP_LOGIN_HANDOFF__.active === true
    );
  } catch (_) {
    return false;
  }
}

function markLoginHandoffWindow(method = "login") {
  try {
    if (typeof window !== "undefined") {
      window.__LOOP_LOGIN_HANDOFF__ = {
        active: true,
        method: String(method || "login"),
        startedAt: Date.now(),
      };
    }
  } catch (_) {}
}

function clearLoginHandoffWindow() {
  try {
    if (typeof window !== "undefined") {
      delete window.__LOOP_LOGIN_HANDOFF__;
    }
  } catch (_) {}
}

export default function Login({ authReady = true, isLoggedIn = false }) {
  useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const from = getLoginRedirectTarget(location, "/");
  const registerTarget = from && from !== "/" ? `/register?next=${encodeURIComponent(String(from))}` : "/register";
  const capacitorPlatform = getCapacitorPlatform();
  const isIosRuntime = capacitorPlatform === "ios" || isIosCapacitor();
  const isAndroidRuntime = capacitorPlatform === "android";
  const isNativeRuntime = isIosRuntime || isAndroidRuntime;

  const showGoogleButton = true;
  const showAppleButton = !isAndroidRuntime;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [loadingApple, setLoadingApple] = useState(false);
  const [loginHandoffPending, setLoginHandoffPending] = useState(() => readLoginHandoffWindow());
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(() => {
    try {
      return location?.state?.info || "";
    } catch (_) {
      return "";
    }
  });

  const beginLoginHandoff = (method) => {
    markLoginHandoffWindow(method);
    setLoginHandoffPending(true);
  };

  const finishLoginHandoff = () => {
    clearLoginHandoffWindow();
    setLoginHandoffPending(false);
  };

  useEffect(() => {
    if (authReady !== true || !isLoggedIn || loginHandoffPending) return undefined;

    let cancelled = false;
    Promise.resolve().then(async () => {
      if (cancelled) return;
      await schedulePostAuthRedirect(navigate, from || "/", {
        replace: true,
        delayMs: 0,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [authReady, isLoggedIn, loginHandoffPending, navigate, from]);

  useEffect(() => {
    if (!isNativeRuntime || typeof document === "undefined") return undefined;

    const body = document.body;
    const html = document.documentElement;

    body.classList.add("loop-login-route");
    html.classList.add("loop-login-route-root");

    if (isAndroidRuntime) {
      body.classList.add("loop-login-android");
      html.classList.add("loop-login-android");
    }

    if (isIosRuntime) {
      body.classList.add("loop-login-ios");
      html.classList.add("loop-login-ios");
    }

    // LOOP NATIVE LOGIN STATUSBAR FIX V3
    // El diagnóstico mostró safeAreaTop=27px / safeAreaBottom=48px y StatusBar disponible.
    // Esta pantalla usa fondo claro, por eso forzamos status bar claro + íconos oscuros,
    // removemos cualquier pseudo-header superior verde y hacemos scroll interno del card
    // para que Android navigation guard nunca tape el contenido.
    const applyLoginSystemBars = () => {
      try {
        StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
        StatusBar.setBackgroundColor({ color: "#f8fafc" }).catch(() => {});
        StatusBar.setStyle({ style: "DARK" }).catch(() => {});
      } catch (_) {
        try {
          const NativeStatusBar = window.Capacitor?.Plugins?.StatusBar;
          NativeStatusBar?.setOverlaysWebView?.({ overlay: false });
          NativeStatusBar?.setBackgroundColor?.({ color: "#f8fafc" });
          NativeStatusBar?.setStyle?.({ style: "DARK" });
        } catch (__) {}
      }
    };

    applyLoginSystemBars();
    // Native tablets sometimes keep the previous route status/nav color for one frame.
    // Re-applying after layout settles avoids the green bar leaking into Login.
    const loginSystemBarTimers = [140, 520, 1200].map((ms) => window.setTimeout(applyLoginSystemBars, ms));

    // LOOP AUTH KEYBOARD VIEWPORT FIT K2
    // visualViewport tracks the usable canvas while the software keyboard is open.
    // Keep a stable pre-keyboard height so this works whether Android resizes the
    // WebView (adjustResize) or only shrinks the visual viewport.
    let loginViewportRaf = 0;
    let loginViewportTimers = [];
    let loginStableViewportHeight = 0;
    let loginKeyboardWasOpen = false;
    let loginLastFocusedElement = null;

    const isLoginEditableElement = (node) => {
      try {
        if (!node || typeof node.matches !== "function") return false;
        return node.matches("input, textarea, select, [contenteditable='true']");
      } catch (_) {
        return false;
      }
    };

    const syncLoginViewport = () => {
      try {
        const visualViewport = window.visualViewport || null;
        const measuredHeight = Math.round(
          visualViewport?.height ||
          window.innerHeight ||
          document.documentElement?.clientHeight ||
          0
        );
        const viewportHeight = Math.max(320, measuredHeight || 0);
        const activeElement = document.activeElement;
        const editableFocused = isLoginEditableElement(activeElement);

        if (!editableFocused || loginStableViewportHeight <= 0) {
          loginStableViewportHeight = viewportHeight;
        }

        const keyboardShrink = Math.max(0, loginStableViewportHeight - viewportHeight);
        const keyboardThreshold = Math.max(110, Math.round(loginStableViewportHeight * 0.16));
        const keyboardOpen = editableFocused && keyboardShrink >= keyboardThreshold;

        [html, body].forEach((node) => {
          node.style.setProperty("--loop-login-viewport-h", `${viewportHeight}px`);
          node.style.setProperty("--loop-login-keyboard-shrink", `${keyboardShrink}px`);
          node.classList.toggle("loop-login-keyboard-open", keyboardOpen);
        });

        if (keyboardOpen && activeElement && (!loginKeyboardWasOpen || loginLastFocusedElement !== activeElement)) {
          const focused = activeElement;
          loginViewportTimers.push(window.setTimeout(() => {
            try {
              focused.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
            } catch (_) {
              try { focused.scrollIntoView(false); } catch (__) {}
            }
          }, 90));
        }

        loginKeyboardWasOpen = keyboardOpen;
        loginLastFocusedElement = editableFocused ? activeElement : null;
      } catch (_) {}
    };

    const scheduleLoginViewportSync = () => {
      try {
        if (loginViewportRaf) window.cancelAnimationFrame(loginViewportRaf);
        loginViewportRaf = window.requestAnimationFrame(syncLoginViewport);
      } catch (_) {
        syncLoginViewport();
      }
    };

    const scheduleLoginKeyboardAnimationSync = () => {
      scheduleLoginViewportSync();
      [60, 160, 320, 520].forEach((ms) => {
        loginViewportTimers.push(window.setTimeout(scheduleLoginViewportSync, ms));
      });
    };

    const resetLoginViewportBaseline = () => {
      loginStableViewportHeight = 0;
      scheduleLoginKeyboardAnimationSync();
    };

    scheduleLoginViewportSync();
    window.addEventListener("resize", scheduleLoginViewportSync);
    window.addEventListener("orientationchange", resetLoginViewportBaseline);
    window.visualViewport?.addEventListener?.("resize", scheduleLoginViewportSync);
    window.visualViewport?.addEventListener?.("scroll", scheduleLoginViewportSync);
    document.addEventListener("focusin", scheduleLoginKeyboardAnimationSync);
    document.addEventListener("focusout", scheduleLoginKeyboardAnimationSync);

    let style = document.getElementById("loop-login-native-fit-style");
    if (!style) {
      style = document.createElement("style");
      style.id = "loop-login-native-fit-style";
      document.head.appendChild(style);
    }

    style.textContent = `
      html.loop-login-route-root,
      body.loop-login-route {
        --loop-login-page-bg: #f8fafc;
        --loop-login-viewport-h: 100dvh;
        --loop-login-keyboard-shrink: 0px;
        --loop-login-top-reserved: max(8px, env(safe-area-inset-top, 0px));
        /*
         * Android native note:
         * safeAreaBottom was measured at ~48px on the device, while the app also
         * paints a dark navigation guard behind Android's system buttons.
         * We reserve a hard floor so content never lands below those buttons.
         */
        --loop-login-android-bottom-reserved: max(118px, calc(env(safe-area-inset-bottom, 0px) + 70px));
        --loop-login-ios-bottom-reserved: max(76px, calc(env(safe-area-inset-bottom, 0px) + 42px));
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        margin-top: 0 !important;
        background: var(--loop-login-page-bg) !important;
        overflow: hidden !important;
        overscroll-behavior-y: none !important;
      }

      body.loop-login-route::before {
        content: "" !important;
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        height: max(28px, env(safe-area-inset-top, 0px)) !important;
        background: var(--loop-login-page-bg) !important;
        z-index: 2147483000 !important;
        pointer-events: none !important;
      }

      body.loop-login-route .loop-mobile-header-v2,
      body.loop-login-route .loop-ref-header,
      body.loop-login-route header.loop-mobile-header-v2 {
        display: none !important;
      }

      body.loop-login-route #root,
      body.loop-login-route .App {
        height: var(--loop-login-viewport-h, 100dvh) !important;
        min-height: var(--loop-login-viewport-h, 100dvh) !important;
        max-height: var(--loop-login-viewport-h, 100dvh) !important;
        background: var(--loop-login-page-bg) !important;
        overflow: hidden !important;
      }

      body.loop-login-route .loop-login-root {
        position: relative !important;
        height: var(--loop-login-viewport-h, 100dvh) !important;
        min-height: var(--loop-login-viewport-h, 100dvh) !important;
        max-height: var(--loop-login-viewport-h, 100dvh) !important;
        align-items: flex-start !important;
        justify-content: center !important;
        padding-top: 8px !important;
        padding-bottom: var(--loop-login-android-bottom-reserved) !important;
        overflow: hidden !important;
        box-sizing: border-box !important;
        -webkit-overflow-scrolling: touch !important;
        overscroll-behavior-y: contain !important;
        background:
          radial-gradient(circle at top left, rgba(0,103,95,0.10), transparent 34%),
          linear-gradient(180deg, #f8fafc 0%, #ffffff 58%, #f8fafc 100%) !important;
      }

      body.loop-login-route.loop-login-android .loop-login-root {
        padding-top: 8px !important;
        padding-bottom: var(--loop-login-android-bottom-reserved) !important;
      }

      body.loop-login-route.loop-login-ios .loop-login-root {
        padding-top: calc(10px + env(safe-area-inset-top, 0px)) !important;
        padding-bottom: var(--loop-login-ios-bottom-reserved) !important;
      }

      /*
       * Instead of letting the whole page scroll under Android's fixed nav guard,
       * the card itself scrolls inside the safe visible area.
       */
      body.loop-login-route .loop-login-card {
        position: relative !important;
        z-index: 1 !important;
        margin-top: 0 !important;
        max-height: calc(var(--loop-login-viewport-h, 100dvh) - 8px - var(--loop-login-android-bottom-reserved)) !important;
        overflow-y: auto !important;
        overscroll-behavior-y: contain !important;
        -webkit-overflow-scrolling: touch !important;
        scroll-padding-bottom: 28px !important;
      }

      body.loop-login-route.loop-login-ios .loop-login-card {
        max-height: calc(var(--loop-login-viewport-h, 100dvh) - 10px - env(safe-area-inset-top, 0px) - var(--loop-login-ios-bottom-reserved)) !important;
      }

      body.loop-login-route .loop-login-card::-webkit-scrollbar {
        width: 0 !important;
        height: 0 !important;
      }

      body.loop-login-route .loop-login-card {
        scrollbar-width: none !important;
      }

      body.loop-login-route .loop-login-root::before {
        display: none !important;
        content: none !important;
        background: transparent !important;
      }



      /* =========================================================
         LOOP LOGIN NATIVE ADAPTIVE FIT V4
         Phone: keep the card scroll-safe above Android navigation.
         Tablet/native large viewport: center the card in the visible canvas
         and shrink the bottom reservation so Login does not look phone-sized
         pinned to the top with a huge empty area below.
         ========================================================= */
      body.loop-login-route .loop-login-root {
        width: 100% !important;
        box-sizing: border-box !important;
      }

      body.loop-login-route .loop-login-card {
        width: min(100%, 448px) !important;
        max-width: 448px !important;
        box-sizing: border-box !important;
      }

      /* Tablet portrait / native large canvas */
      @media (min-width: 700px) and (min-height: 720px) {
        body.loop-login-route.loop-login-android {
          --loop-login-android-bottom-reserved: max(76px, calc(env(safe-area-inset-bottom, 0px) + 30px));
        }

        body.loop-login-route.loop-login-ios {
          --loop-login-ios-bottom-reserved: max(58px, calc(env(safe-area-inset-bottom, 0px) + 28px));
        }

        body.loop-login-route .loop-login-root {
          align-items: center !important;
          justify-content: center !important;
          padding-left: clamp(22px, 4vw, 54px) !important;
          padding-right: clamp(22px, 4vw, 54px) !important;
          padding-top: max(18px, calc(env(safe-area-inset-top, 0px) + 10px)) !important;
          padding-bottom: var(--loop-login-android-bottom-reserved) !important;
        }

        body.loop-login-route.loop-login-ios .loop-login-root {
          padding-bottom: var(--loop-login-ios-bottom-reserved) !important;
        }

        body.loop-login-route .loop-login-card {
          width: min(100%, 520px) !important;
          max-width: 520px !important;
          max-height: min(860px, calc(var(--loop-login-viewport-h, 100dvh) - max(18px, calc(env(safe-area-inset-top, 0px) + 10px)) - var(--loop-login-android-bottom-reserved) - 18px)) !important;
          border-radius: 32px !important;
        }

        body.loop-login-route.loop-login-ios .loop-login-card {
          max-height: min(860px, calc(var(--loop-login-viewport-h, 100dvh) - max(18px, calc(env(safe-area-inset-top, 0px) + 10px)) - var(--loop-login-ios-bottom-reserved) - 18px)) !important;
        }
      }

      /* Native tablet / split-screen touch viewports that report desktop width */
      @media (pointer: coarse) and (min-width: 900px) and (max-width: 1600px) {
        body.loop-login-route .loop-login-root {
          align-items: center !important;
        }

        body.loop-login-route .loop-login-card {
          width: min(52vw, 540px) !important;
          max-width: 540px !important;
        }
      }

      /* Landscape or short-height mode: compact vertical rhythm and keep everything inside. */
      @media (max-height: 760px) {
        body.loop-login-route .loop-login-root {
          align-items: center !important;
          padding-top: max(8px, env(safe-area-inset-top, 0px)) !important;
          padding-bottom: max(58px, calc(env(safe-area-inset-bottom, 0px) + 22px)) !important;
        }

        body.loop-login-route .loop-login-card {
          max-height: calc(var(--loop-login-viewport-h, 100dvh) - max(8px, env(safe-area-inset-top, 0px)) - max(58px, calc(env(safe-area-inset-bottom, 0px) + 22px)) - 8px) !important;
        }

        body.loop-login-route .loop-login-card > div:first-child {
          padding-top: 20px !important;
          padding-bottom: 12px !important;
        }

        body.loop-login-route .loop-auth-brand {
          width: 138px !important;
          height: 48px !important;
          margin-bottom: 10px !important;
        }

        body.loop-login-route .loop-auth-brand img {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          object-position: center !important;
        }

        body.loop-login-route .loop-login-card h2 {
          font-size: 26px !important;
          letter-spacing: -0.05em !important;
        }

        body.loop-login-route .loop-login-card form {
          gap: 12px !important;
        }
      }

      @media (max-width: 430px) {
        body.loop-login-route .loop-login-root {
          padding-left: 16px !important;
          padding-right: 16px !important;
        }

        body.loop-login-route .loop-login-card {
          border-radius: 28px !important;
        }
      }

      /* =========================================================
         LOOP AUTH KEYBOARD VIEWPORT FIT K2
         When an input owns the keyboard, use the actual visual viewport and
         remove the large navigation reservation. The card remains the scroller.
         ========================================================= */
      body.loop-login-route.loop-login-keyboard-open {
        --loop-login-android-bottom-reserved: max(10px, env(safe-area-inset-bottom, 0px));
        --loop-login-ios-bottom-reserved: max(10px, env(safe-area-inset-bottom, 0px));
      }

      body.loop-login-route.loop-login-keyboard-open .loop-login-root {
        align-items: flex-start !important;
        justify-content: center !important;
        padding-top: max(4px, env(safe-area-inset-top, 0px)) !important;
        padding-bottom: max(10px, env(safe-area-inset-bottom, 0px)) !important;
      }

      body.loop-login-route.loop-login-keyboard-open .loop-login-card {
        max-height: calc(var(--loop-login-viewport-h, 100dvh) - max(4px, env(safe-area-inset-top, 0px)) - max(10px, env(safe-area-inset-bottom, 0px))) !important;
        scroll-padding-top: 12px !important;
        scroll-padding-bottom: 18px !important;
      }

      body.loop-login-route.loop-login-keyboard-open .loop-login-card > div:first-child {
        padding-top: 16px !important;
        padding-bottom: 10px !important;
      }

      body.loop-login-route.loop-login-keyboard-open .loop-auth-brand {
        width: 128px !important;
        height: 44px !important;
        margin-bottom: 8px !important;
      }

      body.loop-login-route.loop-login-keyboard-open .loop-login-card h2 {
        font-size: 24px !important;
        line-height: 1.05 !important;
      }

      body.loop-login-route.loop-login-keyboard-open .loop-login-card > div:first-child p {
        margin-top: 6px !important;
        line-height: 1.35 !important;
      }

      body.loop-login-route.loop-login-keyboard-open .loop-login-card form {
        gap: 10px !important;
      }
    `;

    try {
      window.__LOOP_LOGIN_FIT_DEBUG__ = () => {
        const root = document.querySelector(".loop-login-root");
        const card = document.querySelector(".loop-login-card");
        const rootStyle = root ? getComputedStyle(root) : null;
        const cardStyle = card ? getComputedStyle(card) : null;
        return {
          platform: getCapacitorPlatform(),
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          visualViewport: window.visualViewport ? `${Math.round(window.visualViewport.width)}x${Math.round(window.visualViewport.height)}` : "none",
          dpr: window.devicePixelRatio || 1,
          bodyClasses: document.body.className,
          max899: window.matchMedia("(max-width: 899px)").matches,
          tabletFit: window.matchMedia("(min-width: 700px) and (min-height: 720px)").matches,
          coarseTablet: window.matchMedia("(pointer: coarse) and (min-width: 900px) and (max-width: 1600px)").matches,
          shortHeight: window.matchMedia("(max-height: 760px)").matches,
          rootAlignItems: rootStyle?.alignItems || "",
          rootPaddingTop: rootStyle?.paddingTop || "",
          rootPaddingBottom: rootStyle?.paddingBottom || "",
          cardWidth: cardStyle?.width || "",
          cardMaxWidth: cardStyle?.maxWidth || "",
          cardMaxHeight: cardStyle?.maxHeight || "",
          cardTop: card ? Math.round(card.getBoundingClientRect().top) : null,
          cardBottom: card ? Math.round(card.getBoundingClientRect().bottom) : null,
          cardHeight: card ? Math.round(card.getBoundingClientRect().height) : null,
          keyboardOpen: body.classList.contains("loop-login-keyboard-open"),
          keyboardViewportHeight: getComputedStyle(body).getPropertyValue("--loop-login-viewport-h").trim(),
          keyboardShrink: getComputedStyle(body).getPropertyValue("--loop-login-keyboard-shrink").trim(),
          activeElement: document.activeElement?.tagName || "",
        };
      };
    } catch (_) {}

    return () => {
      loginSystemBarTimers.forEach((timer) => {
        try { window.clearTimeout(timer); } catch (_) {}
      });
      try { delete window.__LOOP_LOGIN_FIT_DEBUG__; } catch (_) {}
      try {
        if (loginViewportRaf) window.cancelAnimationFrame(loginViewportRaf);
        loginViewportTimers.forEach((timer) => window.clearTimeout(timer));
        window.removeEventListener("resize", scheduleLoginViewportSync);
        window.removeEventListener("orientationchange", resetLoginViewportBaseline);
        window.visualViewport?.removeEventListener?.("resize", scheduleLoginViewportSync);
        window.visualViewport?.removeEventListener?.("scroll", scheduleLoginViewportSync);
        document.removeEventListener("focusin", scheduleLoginKeyboardAnimationSync);
        document.removeEventListener("focusout", scheduleLoginKeyboardAnimationSync);
      } catch (_) {}

      body.classList.remove("loop-login-route", "loop-login-android", "loop-login-ios", "loop-login-keyboard-open");
      html.classList.remove("loop-login-route-root", "loop-login-android", "loop-login-ios", "loop-login-keyboard-open");
      [body, html].forEach((node) => {
        try {
          node.style.removeProperty("--loop-login-viewport-h");
          node.style.removeProperty("--loop-login-keyboard-shrink");
        } catch (_) {}
      });

      try {
        if (isAndroidRuntime) {
          StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
          StatusBar.setBackgroundColor({ color: "#00675F" }).catch(() => {});
          StatusBar.setStyle({ style: "LIGHT" }).catch(() => {});
        } else if (isIosRuntime) {
          StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
          StatusBar.setBackgroundColor({ color: "#003F3A" }).catch(() => {});
          StatusBar.setStyle({ style: "DARK" }).catch(() => {});
        }
      } catch (_) {}
    };
  }, [isNativeRuntime, isAndroidRuntime, isIosRuntime]);

  async function persistFromFirebaseUser(user, source) {
    if (!user) return;
    try {
      const idToken = await user.getIdToken();
      const tokenIdentity = extractIdentityFromIdToken(idToken);

      persistManualAuth({
        idToken,
        refreshToken: user.refreshToken || null,
        expiresIn: "3600",
        uid: user.uid || tokenIdentity.uid || null,
        obtainedAt: new Date().toISOString(),
        source,
        email: user.email || tokenIdentity.email || "",
        displayName: user.displayName || tokenIdentity.displayName || "",
      });

      broadcastManualAuthChange("manual-auth-detected");
      broadcastManualAuthChange("manual-auth-changed");
      try { window.dispatchEvent(new Event("loop-authenticated")); } catch (_) {}
    } catch (_) {}
  }

  const handleEmailLogin = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError("");
    setSuccess("");

    const emailTrim = (email || "").trim();
    const passwordVal = password || "";

    if (!emailTrim || !passwordVal) {
      setError("Ingresa tu usuario y contraseña.");
      return;
    }

    clearLoopAuthState({ keepPendingRegistration: true });
    beginLoginHandoff("email");
    setLoadingEmail(true);

    try {
      if (isIosCapacitor()) {
        const available = isFirebaseNativeAvailable();
        if (!available) {
          throw new Error("FirebaseNative plugin no está disponible en esta build iOS.");
        }

        const result = await withTimeout(
          FirebaseNative.signInWithEmailPassword({
            email: emailTrim,
            password: passwordVal,
          }),
          20000,
          "native-login-timeout"
        );

        const nativeIdToken = result && result.idToken ? String(result.idToken) : "";
        if (!nativeIdToken) {
          throw new Error("Login nativo OK, pero idToken vino vacío.");
        }

        const tokenIdentity = extractIdentityFromIdToken(nativeIdToken);

        if (tokenIdentity.emailVerified === false) {
          try {
            window.localStorage.setItem("pending_uid", (result && result.uid ? String(result.uid) : "") || tokenIdentity.uid || "");
            window.localStorage.setItem("pending_email", (result && result.email ? String(result.email) : "") || tokenIdentity.email || emailTrim);
          } catch (_) {}

          finishLoginHandoff();
          navigate("/enter-code", {
            replace: true,
            state: {
              uid: (result && result.uid ? String(result.uid) : "") || tokenIdentity.uid || null,
              email: (result && result.email ? String(result.email) : "") || tokenIdentity.email || emailTrim,
            },
          });
          setError("Debes verificar tu correo antes de ingresar.");
          return;
        }

        persistManualAuth({
          idToken: nativeIdToken,
          refreshToken: null,
          expiresIn: "3600",
          uid: (result && result.uid ? String(result.uid) : "") || tokenIdentity.uid || null,
          obtainedAt: new Date().toISOString(),
          source: "ios-native-email",
          email: (result && result.email ? String(result.email) : "") || tokenIdentity.email || emailTrim,
          displayName: (result && result.displayName ? String(result.displayName) : "") || tokenIdentity.displayName || "",
        });

        broadcastManualAuthChange("manual-auth-detected");
        broadcastManualAuthChange("manual-auth-changed");

        const profileResult = await resolveCurrentAuthProfile({
          source: "ios_native_email_login_r31",
          provider: "password",
          displayName:
            (result && result.displayName ? String(result.displayName) : "") ||
            tokenIdentity.displayName ||
            "",
        });
        const target = profileResult?.profileComplete
          ? from
          : `/complete-profile?next=${encodeURIComponent(from || "/")}`;

        await schedulePostAuthRedirect(navigate, target, {
          replace: true,
          delayMs: isIosRuntime ? 120 : 0,
        });
        finishLoginHandoff();
        return;
      }

      const cred = await withTimeout(
        signInWithEmailAndPassword(firebaseAuth, emailTrim, passwordVal),
        20000,
        "web-login-timeout"
      );

      const blockedForVerification = await redirectIfEmailNotVerified({
        user: cred.user,
        navigate,
        setError,
      });

      if (blockedForVerification) {
        finishLoginHandoff();
        return;
      }

      await persistFromFirebaseUser(cred.user, "web-sdk-email");
      const profileResult = await resolveCurrentAuthProfile({
        source: "web_email_login_r31",
        provider: "password",
        displayName: cred.user?.displayName || "",
      });
      const target = profileResult?.profileComplete
        ? from
        : `/complete-profile?next=${encodeURIComponent(from || "/")}`;
      await schedulePostAuthRedirect(navigate, target, {
        replace: true,
        delayMs: isIosRuntime ? 120 : 0,
      });
      finishLoginHandoff();
    } catch (e2) {
      finishLoginHandoff();
      setError(friendlyError(e2));
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleResetPassword = async () => {
    setError("");
    setSuccess("");

    const emailTrim = (email || "").trim();

    if (!emailTrim) {
      setError("Escribe tu correo electrónico para enviarte el enlace de recuperación.");
      return;
    }

    try {
      firebaseAuth.languageCode = getLanguage();
      await withTimeout(
        sendPasswordResetEmail(firebaseAuth, emailTrim),
        20000,
        "password-reset-timeout"
      );

      setSuccess("Te enviamos un correo para recuperar tu contraseña.");
    } catch (e) {
      setError(friendlyError(e));
    }
  };

  const handleAppleLogin = async () => {
    setError("");
    setSuccess("");

    if (isAndroidRuntime) return;

    beginLoginHandoff("apple");
    setLoadingApple(true);
    try {
      const result = await signInWithAppleAcrossPlatforms();
      const target = result?.profileComplete
        ? from
        : `/complete-profile?next=${encodeURIComponent(from || "/")}`;
      await schedulePostAuthRedirect(navigate, target, {
        replace: true,
        delayMs: isIosRuntime ? 120 : 0,
      });
      finishLoginHandoff();
    } catch (e) {
      finishLoginHandoff();
      setError(socialAuthErrorMessage(e));
    } finally {
      setLoadingApple(false);
    }
  };

  const handleAppleOtherAccountInfo = () => {
    setError("");
    setSuccess(
      "Apple no permite elegir cuenta dentro de la app como Google. Para usar otro Apple ID, cambia el Apple ID del dispositivo o cierra sesión desde Ajustes de iOS e intenta nuevamente."
    );
  };

  const handleGoogleLogin = async () => {
    setError("");
    setSuccess("");
    beginLoginHandoff("google");
    setLoadingGoogle(true);

    try {
      const result = await signInWithGoogleAcrossPlatforms();
      const target = result?.profileComplete
        ? from
        : `/complete-profile?next=${encodeURIComponent(from || "/")}`;
      await schedulePostAuthRedirect(navigate, target, {
        replace: true,
        delayMs: isIosRuntime ? 120 : 0,
      });
      finishLoginHandoff();
    } catch (e) {
      finishLoginHandoff();
      console.error("[Login] Google login failed", e);
      setError(socialAuthErrorMessage(e));
    } finally {
      setLoadingGoogle(false);
    }
  };

  const authTransitionActive =
    authReady === false ||
    loginHandoffPending ||
    (authReady === true && isLoggedIn);

  if (authTransitionActive) {
    const isCheckingSession = authReady === false && !loginHandoffPending;
    return (
      <div
        id="loop-login-root"
        className={`loop-login-root min-h-screen flex ${isNativeRuntime ? "items-start justify-center px-4 py-0" : "items-center justify-center px-4 py-8"}`}
        style={{
          background:
            "radial-gradient(circle at top left, rgba(0,103,95,0.10), transparent 34%), linear-gradient(180deg, #f8fafc 0%, #ffffff 58%, #f8fafc 100%)",
        }}
      >
        <div
          id="loop-login-card"
          className="loop-login-card w-full max-w-md overflow-hidden rounded-[30px] border border-slate-200 bg-white"
          style={{ boxShadow: "0 24px 70px rgba(15, 23, 42, 0.10)" }}
        >
          <div className="px-7 py-10 text-center">
            <div className="loop-auth-brand mx-auto mb-6 flex h-[58px] w-[164px] items-center justify-center overflow-hidden">
              <img
                src="/logo3.png"
                alt="Loop"
                className="h-full w-full object-cover object-center"
                draggable="false"
              />
            </div>

            <div
              className="mx-auto mb-5 h-9 w-9 animate-spin rounded-full border-[3px] border-emerald-100 border-t-emerald-700"
              aria-hidden="true"
            />

            <h2 className="text-[24px] font-black tracking-[-0.04em] text-slate-950">
              {isCheckingSession ? "Revisando tu sesión..." : "Entrando a Loop..."}
            </h2>
            <p className="mx-auto mt-2 max-w-xs text-sm font-semibold leading-5 text-slate-500">
              {isCheckingSession
                ? "Estamos comprobando si ya tienes una sesión activa."
                : "Tu acceso está en proceso. No necesitas volver a escribir tus datos."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id="loop-login-root"
      className={`loop-login-root min-h-screen flex ${isNativeRuntime ? "items-start justify-center px-4 py-0" : "items-center justify-center px-4 py-8"}`}
      style={{
        background:
          "radial-gradient(circle at top left, rgba(0,103,95,0.10), transparent 34%), linear-gradient(180deg, #f8fafc 0%, #ffffff 58%, #f8fafc 100%)",
      }}
    >
      <div
        id="loop-login-card"
        className="loop-login-card w-full max-w-md overflow-hidden rounded-[30px] border border-slate-200 bg-white"
        style={{
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.10)",
        }}
      >
        <div className="px-7 pt-8 pb-5">
          <div className="loop-auth-brand mx-auto mb-5 flex h-[58px] w-[164px] items-center justify-center overflow-hidden">
            <img
              src="/logo3.png"
              alt="Loop"
              className="h-full w-full object-cover object-center"
              draggable="false"
            />
          </div>

          <h2 className="text-center text-[32px] font-black tracking-[-0.055em] text-slate-950">{t("Bienvenido a Loop")}</h2>

          <p className="mx-auto mt-2 max-w-xs text-center text-sm font-semibold leading-6 text-slate-500">{t("Entra para gestionar reservas, negocios y calendarios.")}</p>
        </div>

        <div className="px-6 pb-6">
          {error ? (
            <div
              className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
              style={{ whiteSpace: "pre-wrap" }}
            >
              {t(error)}
            </div>
          ) : null}

          {success ? (
            <div
              className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700"
              style={{ whiteSpace: "pre-wrap" }}
            >
              {t(success)}
            </div>
          ) : null}

          {showGoogleButton || showAppleButton ? (
            <div className="space-y-3">
              <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-2.5 shadow-sm">
                {showGoogleButton ? (
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={loadingGoogle || loadingApple || loadingEmail}
                    className="w-full flex items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-slate-900 transition hover:bg-slate-50 active:scale-[0.99] disabled:opacity-60"
                  >
                    <GoogleIcon />
                    <span className="text-sm font-black">
                      {loadingGoogle ? t("Conectando...") : t("Continuar con Google")}
                    </span>
                  </button>
                ) : null}

                {showAppleButton ? (
                  <div className={`${showGoogleButton ? "mt-2.5 border-t border-slate-200 pt-2.5" : ""} space-y-2.5`}>
                    <button
                      type="button"
                      onClick={handleAppleLogin}
                      disabled={loadingApple || loadingGoogle || loadingEmail}
                      className="w-full flex items-center justify-center gap-3 rounded-2xl bg-slate-950 px-4 py-3.5 text-white transition hover:bg-black active:scale-[0.99] disabled:opacity-60"
                    >
                      <AppleIcon />
                      <span className="text-sm font-black">
                        {loadingApple ? t("Conectando...") : t("Continuar con Apple")}
                      </span>
                    </button>

                    {isIosRuntime ? (
                      <button
                        type="button"
                        onClick={handleAppleOtherAccountInfo}
                        disabled={loadingApple || loadingGoogle || loadingEmail}
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 transition hover:bg-slate-50 active:scale-[0.99] disabled:opacity-60"
                      >{t("Usar otro Apple ID")}</button>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <p className="px-3 text-center text-[11px] font-semibold leading-4 text-slate-400">{t("Login seguro con proveedores oficiales. Loop no guarda tu contraseña de Google o Apple.")}</p>
            </div>
          ) : null}

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">{t("o entra con correo")}</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          {typeof navigator !== 'undefined' && /Instagram|FBAN|FBAV/i.test(navigator.userAgent) && <p role="note" className="mb-4 text-sm text-slate-600">{t("Estás en el navegador de Instagram o Facebook. Puedes continuar con tu correo y contraseña. Si Google no abre, usa el menú de esta ventana para abrir el enlace en Chrome o Safari.")}</p>}
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label htmlFor="loop-login-email" className="mb-1.5 block text-sm font-black text-slate-800">{t("Correo electrónico")}</label>
              <input
                id="loop-login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                autoComplete="email"
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-slate-950 outline-none transition placeholder:text-transparent focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                placeholder=""
              />
            </div>

            <div>
              <label htmlFor="loop-login-password" className="mb-1.5 block text-sm font-black text-slate-800">{t("Contraseña")}</label>
              <div className="relative">
                <input
                  id="loop-login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3.5 pl-4 pr-14 text-slate-950 outline-none transition placeholder:text-transparent focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                  placeholder=""
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={showPassword ? t("Ocultar contraseña") : t("Mostrar contraseña")}
                  aria-pressed={showPassword}
                  aria-controls="loop-login-password"
                  title={showPassword ? t("Ocultar contraseña") : t("Mostrar contraseña")}
                  className="absolute inset-y-1 right-1 flex w-11 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                >
                  {showPassword ? (
                    <EyeOff size={20} strokeWidth={2.2} aria-hidden="true" />
                  ) : (
                    <Eye size={20} strokeWidth={2.2} aria-hidden="true" />
                  )}
                </button>
              </div>

              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={handleResetPassword}
                  className="text-sm font-bold text-emerald-700 hover:text-emerald-800"
                >{t("¿Olvidaste tu contraseña?")}</button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loadingEmail || loadingGoogle || loadingApple}
              className="w-full rounded-2xl bg-emerald-700 px-4 py-3.5 text-sm font-black text-white transition hover:bg-emerald-800 active:scale-[0.99] disabled:opacity-60"
              style={{
                boxShadow: "0 14px 30px rgba(0,103,95,0.22)",
              }}
            >
              {loadingEmail ? t("Entrando...") : "Ingresar"}
            </button>
          </form>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-center">
            <span className="text-sm font-semibold text-slate-500">{t("¿No tienes cuenta?")}{" "}
            </span>
            <button
              type="button"
              onClick={() => navigate(registerTarget, { state: { from } })}
              className="text-sm font-black text-emerald-700 hover:text-emerald-800"
            >{t("Crear cuenta")}</button>
          </div>

          <p className="mt-5 text-center text-xs font-medium leading-5 text-slate-400">{t("Al continuar, usarás Loop Reservas bajo nuestros")}{" "}
            <Link to="/terms" target="_blank" rel="noreferrer" className="font-black text-emerald-700 hover:text-emerald-800">{t("Términos")}</Link>,{" "}
            <Link to="/privacy" target="_blank" rel="noreferrer" className="font-black text-emerald-700 hover:text-emerald-800">{t("Privacidad")}</Link>{" "}
            y{" "}
            <Link to="/payments" target="_blank" rel="noreferrer" className="font-black text-emerald-700 hover:text-emerald-800">{t("Pagos")}</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
